/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {useContext, useEffect, useRef, useState} from "react";
import {ToastModule} from "@intuitionrobotics/thunderstorm/frontend";
import {MessagingModule} from "@modules/MessagingModule";
import {ScheduledMessageInfo} from "@app/ir-q-app-common/types/scheduled-messages";
import {Body_SendText} from "@app/ir-q-app-common/types/message";
import {StatusType} from "@app/ir-q-app-common/types/tasks";
import {FONTS} from "@res/fonts";
import {COLORS} from "@res/colors";
import {ContactsContext} from "./ContactsContext";
import {ScheduleDateTimePicker} from "./ScheduleDateTimePicker";

const POLL_INTERVAL_MS = 30_000;

type EditState = {
    taskId: string
    text: string
    executionTimestamp: number
}

type Props = {
    onMessagesSent?: (sentMessages: ScheduledMessageInfo[]) => void
}

export const ScheduledMessagesPanel = ({onMessagesSent}: Props) => {
    const [messages, setMessages] = useState<ScheduledMessageInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [editState, setEditState] = useState<EditState | undefined>(undefined);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | undefined>(undefined);
    const {contacts} = useContext(ContactsContext);

    const prevMessagesRef = useRef<ScheduledMessageInfo[]>([]);
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const onMessagesSentRef = useRef(onMessagesSent);
    useEffect(() => { onMessagesSentRef.current = onMessagesSent; }, [onMessagesSent]);

    const schedulePoll = (pending: ScheduledMessageInfo[]) => {
        if (pollTimerRef.current)
            clearTimeout(pollTimerRef.current);

        const now = Date.now();
        const dueTimes = pending.map(m => m.executionTimestamp).filter(t => t > now);
        const earliest = dueTimes.length > 0 ? Math.min(...dueTimes) : now;
        const delay = Math.max(earliest + POLL_INTERVAL_MS - now, POLL_INTERVAL_MS);

        pollTimerRef.current = setTimeout(() => fetchAndPoll().catch(console.error), delay);
    };

    const fetchAndPoll = async () => {
        setLoading(true);
        try {
            const result = await MessagingModule.queryScheduledMessages();
            const pending = result
                .filter(m => m.status.status === StatusType.ToBeExecuted)
                .sort((a, b) => a.executionTimestamp - b.executionTimestamp);

            const newIds = new Set(pending.map(m => m._id));
            const sentMessages = prevMessagesRef.current.filter(m => !newIds.has(m._id));

            if (sentMessages.length > 0)
                onMessagesSentRef.current?.(sentMessages);

            prevMessagesRef.current = pending;
            setMessages(pending);

            // Keep polling as long as there are messages that are due or upcoming
            if (pending.length > 0)
                schedulePoll(pending);

        } catch (e) {
            ToastModule.toastError("Failed to load scheduled messages");
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchAndPoll().catch(console.error);
        return () => {
            if (pollTimerRef.current)
                clearTimeout(pollTimerRef.current);
        };
    }, []);

    const getMessagePreview = (msg: ScheduledMessageInfo): string => {
        if (msg.messageType === "text") {
            const body = msg.sendBody as Body_SendText;
            return body.text || "Text message";
        }
        return "Media message";
    };

    const getRecipientInfo = (msg: ScheduledMessageInfo): string => {
        const contactIds = msg.sendBody.contactIds;
        if (!contactIds || contactIds.length === 0)
            return "Unknown recipient";
        const names = contactIds.map(id => {
            const contact = contacts[id];
            if (!contact)
                return id;
            return [contact.firstName, contact.lastName].filter(Boolean).join(" ");
        });
        return names.join(", ");
    };

    const startEditing = (msg: ScheduledMessageInfo) => {
        if (confirmDeleteId)
            return;
        setEditState({
            taskId: msg._id,
            text: msg.messageType === "text" ? (msg.sendBody as Body_SendText).text || "" : "",
            executionTimestamp: msg.executionTimestamp
        });
    };

    const handleUpdate = async (taskId: string, text: string, executionTimestamp: number) => {
        try {
            const msg = messages.find(m => m._id === taskId);
            if (!msg)
                return;

            const updatedBody = {...msg.sendBody};
            if (msg.messageType === "text")
                (updatedBody as Body_SendText).text = text;

            await MessagingModule.updateScheduledMessage(taskId, updatedBody, executionTimestamp);
            ToastModule.toastSuccess("Scheduled message updated");
            setEditState(undefined);
            await fetchAndPoll();
        } catch (e) {
            ToastModule.toastError("Failed to update scheduled message");
        }
    };

    const handleDelete = async (taskId: string) => {
        try {
            await MessagingModule.deleteScheduledMessage(taskId);
            ToastModule.toastSuccess("Scheduled message cancelled");
            setConfirmDeleteId(undefined);
            await fetchAndPoll();
        } catch (e) {
            ToastModule.toastError("Failed to cancel scheduled message");
        }
    };

    const renderMessageRow = (msg: ScheduledMessageInfo) => {
        const isEditing = editState?.taskId === msg._id;
        const isConfirmingDelete = confirmDeleteId === msg._id;

        return <div key={msg._id}
                    className={!isEditing && !isConfirmingDelete ? "clickable" : ""}
                    onClick={() => {
                        if (!isEditing && !isConfirmingDelete)
                            startEditing(msg);
                    }}
                    style={{
                        padding: "12px 16px",
                        borderBottom: `1px solid ${COLORS.neutral_gray_2}`,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8
                    }}>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <div style={{flex: 1}} onClick={(e) => isEditing && e.stopPropagation()}>
                    {isEditing
                        ? <input
                            type="text"
                            value={editState.text}
                            onChange={(e) => setEditState({...editState, text: e.target.value})}
                            style={{
                                width: "100%",
                                padding: "4px 8px",
                                border: `1px solid ${COLORS.neutral_gray_2}`,
                                borderRadius: 4,
                                fontSize: 14
                            }}
                        />
                        : FONTS.reg_14(getMessagePreview(msg), {color: COLORS.grays_dark})
                    }
                </div>
                <div style={{marginLeft: 12}}>
                    {FONTS.reg_12(getRecipientInfo(msg), {color: COLORS.grays_4})}
                </div>
            </div>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <div onClick={(e) => isEditing && e.stopPropagation()}>
                    {isEditing
                        ? <ScheduleDateTimePicker
                            initialTimestamp={editState.executionTimestamp}
                            onSchedule={(ts) => {
                                if (ts !== undefined)
                                    setEditState({...editState, executionTimestamp: ts});
                            }}
                        />
                        : FONTS.reg_12(`Scheduled: ${new Date(msg.executionTimestamp).toLocaleString()}`, {color: COLORS.semantic_blue})
                    }
                </div>
                <div style={{display: "flex", gap: 8}} onClick={(e) => e.stopPropagation()}>
                    {isEditing ? <>
                        <div className={"clickable"} onClick={() => handleUpdate(editState.taskId, editState.text, editState.executionTimestamp)}>
                            {FONTS.reg_14("Save", {color: COLORS.semantic_blue, textDecoration: "underline"})}
                        </div>
                        <div className={"clickable"} onClick={() => setEditState(undefined)}>
                            {FONTS.reg_14("Cancel", {color: COLORS.grays_4, textDecoration: "underline"})}
                        </div>
                    </> : isConfirmingDelete ? <>
                        {FONTS.reg_14("Are you sure?", {color: COLORS.red, marginRight: 8})}
                        <div className={"clickable"} onClick={() => handleDelete(msg._id)}>
                            {FONTS.reg_14("Yes, cancel", {color: COLORS.red, textDecoration: "underline"})}
                        </div>
                        <div className={"clickable"} onClick={() => setConfirmDeleteId(undefined)}>
                            {FONTS.reg_14("No", {color: COLORS.grays_4, textDecoration: "underline"})}
                        </div>
                    </> : <div className={"clickable"} onClick={() => setConfirmDeleteId(msg._id)}>
                        {FONTS.reg_14("Cancel message", {color: COLORS.red, textDecoration: "underline"})}
                    </div>}
                </div>
            </div>
        </div>;
    };

    return <div style={{flex: 1, overflow: "auto"}}>
        {loading
            ? <div style={{padding: 24}}>
                {FONTS.reg_14("Loading...", {color: COLORS.grays_4})}
            </div>
            : messages.length === 0
                ? <div style={{padding: 24}}>
                    {FONTS.reg_14("No scheduled messages", {color: COLORS.grays_4})}
                </div>
                : messages.map(renderMessageRow)
        }
    </div>;
};
